/**
 * Pigwords HTTP API client（v1.1）
 * - POST /room → 创建房间，返 { roomCode }
 *
 * URL 解析逻辑与 wsClient 对齐（同域 /room，本地 fallback 8787）
 */

export interface CreateRoomResponse {
  roomCode: string;
}

export class CreateRoomError extends Error {
  constructor(
    public readonly code: string,
    message?: string,
  ) {
    super(message ?? code);
    this.name = 'CreateRoomError';
  }
}

export function resolveApiBase(): string {
  const envUrl = import.meta.env.VITE_API_URL;
  if (envUrl) return String(envUrl).replace(/\/$/, '');
  if (typeof window === 'undefined') return 'http://localhost:8787';
  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1') {
    return `http://${host}:8787`;
  }
  return `${window.location.protocol}//pigwords-api.kerosdev.com`;
}

export async function createRoom(
  fetchFn: typeof fetch = fetch,
): Promise<CreateRoomResponse> {
  const base = resolveApiBase();
  let res: Response;
  try {
    res = await fetchFn(`${base}/room`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({}),
    });
  } catch (err) {
    throw new CreateRoomError('NETWORK_ERROR', String(err));
  }

  if (res.status === 503) {
    throw new CreateRoomError('ROOM_POOL_EXHAUSTED', '房间池已满，稍后重试');
  }
  if (!res.ok) {
    throw new CreateRoomError('HTTP_ERROR', `HTTP ${res.status}`);
  }
  const data = (await res.json()) as Partial<CreateRoomResponse>;
  if (!data.roomCode || !/^\d{4}$/.test(data.roomCode)) {
    throw new CreateRoomError('BAD_RESPONSE', '服务端返回的 roomCode 非法');
  }
  return { roomCode: data.roomCode };
}
